import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import { throwIfNotOk } from "@/lib/api-error";
import { Plus } from "@phosphor-icons/react";
import { PageCard } from "@/components/admin/pages/PageCard";
import { PageDeleteDialog } from "@/components/admin/pages/PageDeleteDialog";
import { PageFilterTabs } from "@/components/admin/pages/PageFilterTabs";
import { BlogPagination } from "@/components/admin/blog/BlogPagination";
import { PageEmptyState } from "@/components/admin/pages/PageEmptyState";
import { PageSkeletonCard } from "@/components/admin/pages/PageSkeletonCard";
import type { PageStatusFilter } from "@/components/admin/pages/PageFilterTabs";
import type { Page } from "@/types";

const PAGES_PER_PAGE = 10;

export function PagesSection() {
  const queryClient = useQueryClient();
  const { page, setPage, searchQuery, debouncedSearch, setSearch, filters, setFilter, pageSize } =
    usePagination<{ statusFilter: PageStatusFilter }>(
      { pageSize: PAGES_PER_PAGE },
      { statusFilter: "" },
    );
  const { statusFilter } = filters;
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "pages", statusFilter, page, debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (statusFilter) params.set("status", statusFilter);
      const res = await fetch(`/api/pages?${params}`);
      if (!res.ok) throw new Error("Failed to fetch pages");
      return res.json() as Promise<{ data: Page[]; total: number; page: number }>;
    },
    placeholderData: (prev) => prev,
  });

  const pages = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/pages/${id}/update`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju stranice");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "pages"] });
      queryClient.invalidateQueries({ queryKey: ["pages"] });
      setDeletingId(null);
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/dashboard/admin/pages/new">
            <Plus className="size-4" />
            Nova stranica
          </Link>
        </Button>
      </div>

      <PageFilterTabs
        statusFilter={statusFilter}
        searchQuery={searchQuery}
        onStatusChange={(v) => setFilter("statusFilter", v)}
        onSearchChange={setSearch}
      />

      {isLoading && pages.length === 0 ? (
        <div className="flex flex-col gap-4">
          {[...Array(3)].map((_, i) => (
            <PageSkeletonCard key={i} />
          ))}
        </div>
      ) : pages.length === 0 ? (
        <PageEmptyState status={statusFilter} hasSearch={debouncedSearch.length > 0} />
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {pages.map((p) => (
              <PageCard key={p.id} page={p} onDelete={() => setDeletingId(p.id)} />
            ))}
          </div>

          {totalPages > 1 && (
            <BlogPagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={PAGES_PER_PAGE}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {deletingId && (
        <PageDeleteDialog
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deletingId)}
          onClose={() => setDeletingId(null)}
        />
      )}
    </div>
  );
}
